import React, { useEffect, useRef, useState } from 'react';
import Logo from "../../../../images/logo.png";
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import moment from 'moment';
import LatoRegular from "../../../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../../../utils/fonts/Lato-Bold.ttf';
import { formatNumber } from '../../../../utils/functionalUtils';
import { formatWeighbridgeWeightMt, mapWeighbridgeWeightsToGrn } from '../../../../utils/weighbridgeUtils';

// Register fonts
Font.register({
    family: 'Lato',
    fonts: [
        { src: LatoRegular },
        { src: LatoBold, fontWeight: 'bold' }
    ]
});

// Define styles
const styles = StyleSheet.create({
    page: {
        padding: 15,
        fontFamily: 'Lato',
        fontSize: 10,
        color: '#333',
        backgroundColor: '#fff'
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 5,
        borderBottom: '1px solid #e0e0e0'
    },
    logoContainer: {
        width: 100
    },
    addressText: {
        fontSize: 9,
        marginVertical: 2,
        color: '#555'
    },
    grnBadge: {
        backgroundColor: '#2c3782',
        padding: 10,
        color: 'white',
        borderRadius: 4,
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        marginRight: 20
    },
    detailRow: {
        flexDirection: 'row',
        marginVertical: 2
    },
    label: {
        fontWeight: 'bold',
        width: '35%'
    },
    value: {
        width: '65%'
    },
    sectionDivider: {
        borderTop: '1px solid #526484',
        marginVertical: 15,
        width: '100%'
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#2c3782',
        marginBottom: 8
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#2c3782',
        color: 'white',
        textAlign: 'center',
        fontSize: 10,
        fontWeight: 'bold'
    },
    tableCell: {
        padding: 3,
        border: '1px solid #526484',
        textAlign: 'center'
    },
    tableRow: {
        flexDirection: 'row',
        fontSize: 8
    },
    signatureSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 12,
        width: '100%'
    },
    signatureField: {
        marginTop: 12,
        borderTop: '1px solid #333',
        width: '60%',
        paddingTop: 4
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        fontSize: 8,
        color: '#666',
        textAlign: 'center',
        borderTop: '1px solid #e0e0e0',
        paddingTop: 8
    }
});

const GoodsReceivedPDF = ({ page_data }) => {
    const targetRef = useRef();
    const [pdf_data, setPDFData] = useState(page_data);
    const [total_density_capacity, setTotalDensityCapacity] = useState("--");
    const [total_base_capacity, setTotalBaseCapacity] = useState("");

    useEffect(() => {
        if (page_data) {
            const totalCapacitySum = page_data?.goods_received_products?.reduce((sum, item) => {
                return sum + parseFloat(item?.total_capacity_in_mt || 0);
            }, 0);

            const totalDensityCapacitySum = page_data?.goods_received_products?.reduce((sum, item) => {
                return sum + parseFloat(item?.uom_details?.total_density_kilos || 0);
            }, 0);

            setTotalDensityCapacity(totalDensityCapacitySum);
            setTotalBaseCapacity(totalCapacitySum);
        }
        setPDFData(page_data);
    }, [page_data]);

    // ------------------------------------------------------
    // SAFE WEIGHBRIDGE DATA MERGE
    // Supports both:
    // 1. backend continue_processing_data.weigh_bridge_ticket
    // 2. selected weighbridge values merged into page_data
    // ------------------------------------------------------
    const rawWeighBridgeTicket = {
        ...(pdf_data?.weigh_bridge_ticket || {}),
        weighbridge_ticket_id:
            pdf_data?.weighbridge_ticket_id ||
            pdf_data?.weigh_bridge_ticket?.weighbridge_ticket_id ||
            pdf_data?.weigh_bridge_ticket?.weigh_bridge_ticket_id ||
            "",
        weigh_bridge_ticket_number:
            pdf_data?.weigh_bridge_ticket_number ||
            pdf_data?.weigh_bridge_ticket?.weigh_bridge_ticket_number ||
            "",
        weigh_bridge_ticket_reference_no:
            pdf_data?.weigh_bridge_ticket_reference_no ||
            pdf_data?.weigh_bridge_ticket?.weigh_bridge_ticket_reference_no ||
            "",
        transporter_name:
            pdf_data?.transporter_name ||
            pdf_data?.weigh_bridge_ticket?.transporter_name ||
            "",
        truck_licence_plate:
            pdf_data?.truck_licence_plate ||
            pdf_data?.weigh_bridge_ticket?.truck_licence_plate ||
            "",
        trailer_one:
            pdf_data?.trailer_one ||
            pdf_data?.weigh_bridge_ticket?.trailer_one ||
            "",
        trailer_two:
            pdf_data?.trailer_two ||
            pdf_data?.weigh_bridge_ticket?.trailer_two ||
            "",
        time_in:
            pdf_data?.time_in ||
            pdf_data?.weigh_bridge_ticket?.time_in ||
            "",
        time_out:
            pdf_data?.time_out ||
            pdf_data?.weigh_bridge_ticket?.time_out ||
            "",
        truck_driver_name:
            pdf_data?.truck_driver_name ||
            pdf_data?.weigh_bridge_ticket?.truck_driver_name ||
            "",
        first_weight:
            pdf_data?.first_weight ||
            pdf_data?.weigh_bridge_ticket?.first_weight ||
            "",
        second_weight:
            pdf_data?.second_weight ||
            pdf_data?.weigh_bridge_ticket?.second_weight ||
            "",
        net_weight:
            pdf_data?.net_weight ||
            pdf_data?.weigh_bridge_ticket?.net_weight ||
            "",
        comments:
            pdf_data?.comments ||
            pdf_data?.weigh_bridge_ticket?.comments ||
            "",
    };
    const weighBridgeTicket = {
        ...rawWeighBridgeTicket,
        ...mapWeighbridgeWeightsToGrn(rawWeighBridgeTicket),
    };

    const displayTicketNumber =
        weighBridgeTicket?.weigh_bridge_ticket_reference_no ||
        weighBridgeTicket?.weigh_bridge_ticket_number ||
        "---";

    return (
        <Document
            title={`${pdf_data?.goods_received?.goods_received_ref_number || "GRN"} Goods Received`}
            ref={targetRef}
        >
            <Page size="A4" orientation="landscape" style={styles.page}>
                {/* Header */}
                <View style={styles.header} fixed>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <View style={styles.logoContainer}>
                            <Image src={Logo} />
                        </View>
                        <View style={{ marginLeft: 10 }}>
                            <Text style={styles.addressText}>Plot 10111 off Mumbwa Road, Chinika Area</Text>
                            <Text style={styles.addressText}>P.O. Box 30203, Lusaka, Zambia.</Text>
                            <Text style={styles.addressText}>Mobile: +260 764 299 293</Text>
                            <Text style={styles.addressText}>Email: info@iagzambia.com</Text>
                        </View>
                    </View>

                    <View>
                        <Text style={styles.grnBadge}>GOODS RECEIVED NOTE</Text>
                        <View style={{ marginTop: 8 }}>
                            <View style={styles.detailRow}>
                                <Text style={{ fontWeight: 'bold' }}>G.R.N:</Text>
                                <Text style={{ marginLeft: 5 }}>
                                    {pdf_data?.goods_received?.goods_received_ref_number || "---"}
                                </Text>
                            </View>
                            <View style={styles.detailRow}>
                                <Text style={{ fontWeight: 'bold' }}>Date Received:</Text>
                                <Text style={{ marginLeft: 5 }}>
                                    {pdf_data?.goods_received?.date_created
                                        ? moment(pdf_data?.goods_received?.date_created).format("MMMM Do, YYYY")
                                        : "---"}
                                </Text>
                            </View>
                        </View>
                    </View>
                </View>

                {/* Supplier/Transporter Details */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 }}>
                    <View style={{ width: '48%' }}>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Supplier/Agent:</Text>
                            <Text style={styles.value}>{pdf_data?.consigner?.name || "---"}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Transporter:</Text>
                            <Text style={styles.value}>{weighBridgeTicket?.transporter_name || "---"}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Truck No:</Text>
                            <Text style={styles.value}>{weighBridgeTicket?.truck_licence_plate || "---"}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Trailer No 2:</Text>
                            <Text style={styles.value}>{weighBridgeTicket?.trailer_two || "---"}</Text>
                        </View>
                    </View>

                    <View style={{ width: '48%' }}>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Time In:</Text>
                            <Text style={styles.value}>
                                {weighBridgeTicket?.time_in
                                    ? moment(weighBridgeTicket?.time_in).format("YYYY-MM-DD, h:mm:ss")
                                    : "---"}
                            </Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Time Out:</Text>
                            <Text style={styles.value}>
                                {weighBridgeTicket?.time_out
                                    ? moment(weighBridgeTicket?.time_out).format("YYYY-MM-DD, h:mm:ss")
                                    : "---"}
                            </Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Trailer No 1:</Text>
                            <Text style={styles.value}>{weighBridgeTicket?.trailer_one || "---"}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.sectionDivider} />

                {/* Weigh Bridge Weight */}
                <View style={{ marginBottom: 10 }}>
                    <Text style={styles.sectionTitle}>Weigh Bridge Weight</Text>
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCell, { width: '20%' }]}>Ticket Number</Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>Gross Weight</Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>Tare Weight</Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>Net Weight</Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>Comments</Text>
                    </View>
                    <View style={styles.tableRow}>
                        <Text style={[styles.tableCell, { width: '20%' }]}>
                            {displayTicketNumber}
                        </Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>
                            {formatWeighbridgeWeightMt(weighBridgeTicket?.first_weight)}
                        </Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>
                            {formatWeighbridgeWeightMt(weighBridgeTicket?.second_weight)}
                        </Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>
                            {formatWeighbridgeWeightMt(weighBridgeTicket?.net_weight)}
                        </Text>
                        <Text style={[styles.tableCell, { width: '20%' }]}>
                            {weighBridgeTicket?.comments || "---"}
                        </Text>
                    </View>
                </View>

                {/* Commodity Details */}
                <View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text style={styles.sectionTitle}>Commodity Details</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <View style={{ marginRight: 15 }}>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Base Weight (MT):</Text>
                                <Text style={{ fontSize: 10 }}>
                                    {total_base_capacity
                                        ? `${parseFloat(total_base_capacity).toFixed(2)} MT`
                                        : "---"}
                                </Text>
                            </View>
                            <View>
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Density Weight (MT):</Text>
                                <Text style={{ fontSize: 10 }}>
                                    {total_density_capacity
                                        ? `${parseFloat(total_density_capacity).toFixed(2)} MT`
                                        : "---"}
                                </Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Commodity</Text>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Packet Size</Text>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Unit Weight (KGs)</Text>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Packaging No.</Text>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Total Weight (KGs)</Text>
                        <Text style={[styles.tableCell, { width: '16.67%' }]}>Total Capacity (MT)</Text>
                    </View>

                    {(() => {
                        const goodsReceivedList = pdf_data?.goods_received_products || [];
                        const minItems = 8;

                        const placeholdersNeeded = Math.max(minItems - goodsReceivedList.length, 0);
                        const paddedGoodsReceivedList = [
                            ...goodsReceivedList,
                            ...Array(placeholdersNeeded).fill({ isPlaceholder: true })
                        ];

                        return paddedGoodsReceivedList.map((goods_received, index) => {
                            const isPlaceholder = goods_received.isPlaceholder;

                            return (
                                <View key={index} style={styles.tableRow}>
                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder ? "---" : goods_received?.product_name}
                                    </Text>
                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder ? "---" : goods_received?.packet_size}
                                    </Text>
                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder
                                            ? "---"
                                            : goods_received?.uom_details?.measurement_in_kilos &&
                                              formatNumber(parseFloat(goods_received?.uom_details?.measurement_in_kilos).toFixed(2))}
                                    </Text>

                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder
                                            ? "---"
                                            : goods_received?.total_capacity_selected_measurement &&
                                              formatNumber(parseFloat(goods_received?.total_capacity_selected_measurement).toFixed(2))}
                                    </Text>
                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder
                                            ? "---"
                                            : goods_received?.uom_details?.total_kilo &&
                                              formatNumber(parseFloat(goods_received?.uom_details?.total_kilo).toFixed(2))}
                                    </Text>

                                    <Text
                                        style={[
                                            styles.tableCell,
                                            {
                                                width: '16.67%',
                                                color: isPlaceholder ? '#fff' : '#000',
                                                borderColor: isPlaceholder ? '#fff' : '#526484'
                                            }
                                        ]}
                                    >
                                        {isPlaceholder
                                            ? "---"
                                            : goods_received?.total_capacity_in_mt &&
                                              formatNumber(parseFloat(goods_received?.total_capacity_in_mt).toFixed(2))}
                                    </Text>
                                </View>
                            );
                        });
                    })()}
                </View>

                {/* Signature Section */}
                <View style={styles.signatureSection}>
                    <View style={{ width: '45%' }}>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Driver's Name:</Text>
                            <Text style={styles.value}>{weighBridgeTicket?.truck_driver_name || "---"}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Driver's Signature:</Text>
                            <View style={styles.signatureField}></View>
                        </View>
                    </View>

                    <View style={{ width: '45%' }}>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Received By:</Text>
                            <Text style={styles.value}>{pdf_data?.goods_received?.issued_by_name || "---"}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Text style={styles.label}>Location:</Text>
                            <Text style={styles.value}>
                                {pdf_data?.branch_location?.branch_area
                                    ? `${pdf_data?.branch_location?.branch_area}, ${pdf_data?.branch_location?.branch_physical_address}`
                                    : "---"}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>Generated on {moment().format("MMMM Do, YYYY h:mm:ss a")}</Text>
                    <Text>© {new Date().getFullYear()} UAG Zambia. All Rights Reserved.</Text>
                </View>
            </Page>
        </Document>
    );
};

export default GoodsReceivedPDF;
